import { setLoanDetail } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { anyHeldTypeFlag, DocumentsRefreshInterval } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { IBrokerCondition } from 'pages/Admin'
import { resolveFloaterPageKey } from 'pages/Admin/AdminTools/Configuration/FloaterTabs/constants'
import { DocumentHistoryModal } from 'pages/LoanSubmission/Modals/DocumentHistoryModal'
import { LoanSubmissionCondition, LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getAdminConfig,
  getLoanDetailsReportFieldValues,
  getLoanSubmission,
  getLoanSubmissionUpdatedAt,
  openS3Document,
  updateLoanSubmission,
} from 'services'
import { DocumentFile } from 'stories/components'
import {
  getConfiguredReportHeaders,
  normalizeLoanDetailsFieldConfig,
} from 'stories/layouts/FloaterTabs/loanDetails.constants'
import { confirm, formatTime, isLoanLevelPage } from 'utils'
import { LoanDocumentsChange, notifyLoanDocumentsChanged, onLoanSubmissionChanged } from 'utils/loanSubmissionSync'
import { setLoanNumber } from 'utils/setLoanNumber'

import { DocumentsSlide } from './documents'
import { DocumentsSlideNameType } from './documents-simplify'
import { IneligibleParties } from './IneligibleParties'
import { LoanDetailsSlide } from './loanDetails'
import { LoanEstimateDueDateFloater } from './loanEstimateDueDate'
import { NotesSlide } from './notes'
import { NotifyBorrowerSlide } from './NotifyBorrower'
import { TextMessagesSlide } from './textMessages'
import { UnderwritingSlide } from './underwriting'
import { FloaterTabsMinimizeHandles } from './useFloaterTab'

export function FloaterTabs() {
  const [loading, setLoading] = useState(false)
  const [conditions, setConditions] = useState<LoanSubmissionCondition[]>([])
  const [brokerConditions, setBrokerConditions] = useState<IBrokerCondition[]>([])
  const [currentTemplate, setCurrentTemplate] = useState(-1)
  const [documents, setDocuments] = useState<LoanSubmissionDocument[]>([])
  const [historyDocument, setHistoryDocument] = useState<Record<string, any> | null>(null)
  const [showAllDocumentHistory, setShowAllDocumentHistory] = useState(false)
  const [isLoanPage, setIsLoanPage] = useState(false)
  const [floaterTabConfig, setFloaterTabConfig] = useState<Record<string, any>>({})
  const [loanNo, setLoanNo] = useState<string>('0')
  // Refs, not state: the load guard is read from event/interval callbacks, which must not
  // re-register (and restart the poll) every time a fetch flips these.
  const hasLoadedSubmissionRef = useRef(false)
  const isSubmissionLoadingRef = useRef(false)
  const [openSubmissionTabs, setOpenSubmissionTabs] = useState<Record<string, boolean>>({})
  const submissionUpdatedAtRef = useRef<number>(0)
  const [isSubmissionLoading, setIsSubmissionLoading] = useState(false)

  const dispatch = useDispatch()
  const [auth, loanDetail] = useSelector((state: any) => [state.auth, state.loanDetail])

  const { hasPermission } = usePermissions()
  const canNotUploadDocument = !hasPermission('Loan.Document.Upload')
  const canSeeIneligiblePartytab = hasPermission('Loan.FloatTab.IneligibleParty')
  // The texting floater needs BOTH the Servicing > Text Messages screen permission and the
  // floater tab config (Admin Tools > Configuration > Floater Tabs) to allow it on this page.
  const canSeeTextMessages = hasPermission('Loan.Servicing.TextMessages')

  useEffect(() => {
    if (!auth.token) return
    getAdminConfig('floaterTabs').then((res) => {
      setFloaterTabConfig(res)
    })
  }, [auth.token])

  useEffect(() => {
    setIsLoanPage(isLoanLevelPage(location.pathname))
    if (!isLoanLevelPage(location.pathname)) return

    const loanNumber = setLoanNumber()
    setLoanNo(loanNumber)
  }, [location.pathname])

  useEffect(() => {
    if (!isLoanPage || !Number(loanNo)) return
    hasLoadedSubmissionRef.current = false
    setConditions([])
    setCurrentTemplate(-1)
    setDocuments([])
    setBrokerConditions([])
    submissionUpdatedAtRef.current = 0
    // Clear previous loan's report values until this loan's overview + fetch complete.
    dispatch(setLoanDetail({ loanDetailsReportValues: {} }))
  }, [loanNo, isLoanPage, dispatch])

  // Resolve report placeholder values once when entering a loan (not on every floater open).
  // Wait until loan overview has populated loanDetail for this loan so we don't race the
  // getloanDetailInitialState() reset in loanOverviewAndStepLogic.
  useEffect(() => {
    if (!isLoanPage || !Number(loanNo) || !floaterTabConfig?.loanDetail) return
    if (String(loanDetail?.loanNumber) !== String(loanNo)) return

    const fieldConfig = normalizeLoanDetailsFieldConfig(floaterTabConfig.loanDetail?.fieldConfig)
    const headers = getConfiguredReportHeaders(fieldConfig)
    if (!headers.length) return

    let cancelled = false
    getLoanDetailsReportFieldValues(headers)
      .then((values) => {
        if (!cancelled) dispatch(setLoanDetail({ loanDetailsReportValues: values || {} }))
      })
      .catch(() => {
        if (!cancelled) dispatch(setLoanDetail({ loanDetailsReportValues: {} }))
      })
    return () => {
      cancelled = true
    }
  }, [isLoanPage, loanNo, floaterTabConfig, loanDetail?.loanNumber, dispatch])

  const fetchDocuments = () => {
    setLoading(true)
    getLoanSubmission()
      .then(({ documents }) => {
        setDocuments(documents)
      })
      .finally(() => setLoading(false))
  }

  const loadLoanSubmission = async (force = false) => {
    if ((hasLoadedSubmissionRef.current && !force) || isSubmissionLoadingRef.current) return
    if (!isLoanPage || !Number(loanNo)) return
    isSubmissionLoadingRef.current = true
    setIsSubmissionLoading(true)
    try {
      setLoading(true)
      const { templateNumber, conditions, brokerConditions, documents } = await getLoanSubmission()
      setConditions(conditions)
      setCurrentTemplate(templateNumber)
      setDocuments(documents)
      setLoading(false)
      setBrokerConditions(templateNumber == 0 ? brokerConditions : [])
      hasLoadedSubmissionRef.current = true
    } finally {
      isSubmissionLoadingRef.current = false
      setIsSubmissionLoading(false)
    }
  }

  const onTabVisibilityChange = (tab: string, forceOnOpen = false) => (visible: boolean) => {
    setOpenSubmissionTabs((prev) => ({ ...prev, [tab]: visible }))
    if (visible) loadLoanSubmission(forceOnOpen)
  }

  // Conditions are edited on other screens (Conditions page, condition/task modals) while the
  // floater keeps its own copy, so the panels used to show what they read when first opened.
  // An open panel refetches right away; a closed one is marked stale so the next open is fresh.
  useEffect(() => {
    if (!isLoanPage || !Number(loanNo)) return
    return onLoanSubmissionChanged((changedLoanNumber: number) => {
      if (changedLoanNumber && String(changedLoanNumber) !== String(loanNo)) return
      submissionUpdatedAtRef.current = 0
      if (Object.values(openSubmissionTabs).some(Boolean)) loadLoanSubmission(true)
      else hasLoadedSubmissionRef.current = false
    })
  }, [isLoanPage, loanNo, openSubmissionTabs])

  // Edits by other users (or another browser tab) only show up in the submission's updatedAt, so
  // poll it while the notification panel is open, the same way the documents panel does for docs.
  useEffect(() => {
    if (!isLoanPage || !Number(loanNo)) return
    if (!openSubmissionTabs.notification) return

    let cancelled = false
    const pollUpdatedAt = () => {
      getLoanSubmissionUpdatedAt()
        .then((res) => {
          if (cancelled || !res) return
          if (res !== submissionUpdatedAtRef.current) {
            if (submissionUpdatedAtRef.current) loadLoanSubmission(true)
            submissionUpdatedAtRef.current = res
          }
        })
        .catch((error) => {
          if (!cancelled) console.error('Error fetching loan submission updated at:', error)
        })
    }

    pollUpdatedAt()
    const intervalId = setInterval(pollUpdatedAt, DocumentsRefreshInterval)
    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [isLoanPage, loanNo, openSubmissionTabs.notification])

  const saveDocuments = async (change: LoanDocumentsChange) => {
    await updateLoanSubmission({}, change)
    notifyLoanDocumentsChanged(Number(loanNo), change)
  }

  const onAddDocuments = async (docs: DocumentFile[]) => {
    const newDocs = cloneDeep(documents)
    const addingDocs: LoanSubmissionDocument[] = docs.map((doc) => ({
      conditionNo: 0,
      name: doc.name || doc.fileKey?.split('/').pop() || 'document',
      key: doc.fileKey,
      status: doc.status || 'Not Reviewed',
      category: doc.category || '',
      createdAt: doc.createdAt || Date.now(),
      generated: false,
      createdBy: auth.profile.email,
    }))
    newDocs.push(...addingDocs)
    setDocuments(newDocs)
    setLoading(true)
    await saveDocuments({ update: addingDocs })
    setLoading(false)
  }

  const onUpdateDocuments = async (newDocs: Array<any>, updatedDoc: any) => {
    setDocuments(newDocs)
    setLoading(true)
    await saveDocuments({ update: Array.isArray(updatedDoc) ? updatedDoc : [updatedDoc] })
    setLoading(false)
  }

  const onRemoveDocument = async (document: any, isRemoveCompletely: boolean = false) => {
    const content = (
      <div className="text-gray-900 mb-4 text-md">
        {isRemoveCompletely
          ? 'Are you sure want to delete this document permanently?'
          : 'Are you sure want to delete this document?'}
        <div className="text-gray-800 flex flex-col text-left text-sm">
          {document.conditionNo ? <span>Condition No: {document.conditionNo}</span> : null}
          <span>Document Name: {document.name}</span>
          <span>Uploaded Date: {formatTime(new Date(document.createdAt))}</span>
          {isRemoveCompletely && document.generated ? (
            <span className="mt-2 text-red-700">This document was generated by Nexys. Deleting it removes the copy filed on the loan.</span>
          ) : null}
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(documents)
    const index = newData.findIndex((doc) => doc.conditionNo == document.conditionNo && doc.key == document.key)
    if (index == -1) return
    if (isRemoveCompletely) {
      const removedDocs = newData.splice(index, 1)
      setDocuments(newData)
      setLoading(true)
      await saveDocuments({ delete: removedDocs })
      setLoading(false)
    } else {
      newData[index].conditionNo = 0
      setDocuments(newData)
      setLoading(true)
      await saveDocuments({ update: [newData[index]] })
      setLoading(false)
    }
  }

  const onRemoveDocuments = async (docKeys: string[]) => {
    const newData = cloneDeep(documents)
    const indexes = docKeys
      .map((key) => newData.findIndex((doc) => doc.key == key))
      .filter((v) => v != -1)
      .sort((a, b) => a - b)
    const removedDocs: Record<string, any>[] = []
    indexes.reverse().forEach((index) => {
      const items = newData.splice(index, 1)
      removedDocs.push(...items)
    })
    setLoading(true)
    await saveDocuments({ delete: removedDocs })

    setDocuments(newData)
    setLoading(false)
  }

  const onOpenDocument = async (document: any) => {
    openS3Document(document.key, true)
  }

  const isVisible = (tab: string, props: string = 'visible') => {
    const searchParams = new URLSearchParams(location.search)
    const currentMenu = searchParams.get('menu')
    const pageKey = resolveFloaterPageKey(location.pathname, currentMenu)
    if (!pageKey) return false

    if (
      props === 'visible' &&
      floaterTabConfig[tab]?.[pageKey]?.visible &&
      anyHeldTypeFlag(auth.profile, floaterTabConfig[tab]?.accountTypes)
    )
      return true
    if (props === 'editable' && anyHeldTypeFlag(auth.profile, floaterTabConfig[tab]?.[pageKey]?.editableAccTypes))
      return true
    return false
  }

  if (!isLoanPage || !Number(loanNo)) return <></>

  return (
    <>
      <LayoutLoading show={loading || isSubmissionLoading} />
      {isVisible('loanDetail') && <LoanDetailsSlide />}
      {isVisible('notification') && (
        <NotifyBorrowerSlide
          conditions={conditions}
          brokerProfile={canNotUploadDocument}
          brokerConditions={brokerConditions}
          templateNumber={currentTemplate}
          documents={documents}
          onVisibilityChange={onTabVisibilityChange('notification', true)}
        />
      )}
      {isVisible('underwriting') && <UnderwritingSlide />}
      {isVisible('documents') && (
        <>
          {isVisible('documents', 'editable') ? (
            <DocumentsSlide
              loading={loading}
              setLoading={setLoading}
              documents={documents}
              onAdd={onAddDocuments}
              onUpdate={onUpdateDocuments}
              onRemove={(document: any) => onRemoveDocument(document, true)}
              onSelectedRemove={(selectedKeys: string[]) => onRemoveDocuments(selectedKeys)}
              onOpen={(document: any) => onOpenDocument(document)}
              onOpenDocHistory={setHistoryDocument}
              onOpenAllDocHistory={() => setShowAllDocumentHistory(true)}
              brokerProfile={canNotUploadDocument}
              submitToLoanSetup={loanDetail.submitToLoanSetup}
              onVisibilityChange={onTabVisibilityChange('documents')}
              refetchDocuments={fetchDocuments}
            />
          ) : (
            <DocumentsSlideNameType loadDocs={true} loading={false} position="right" />
          )}
        </>
      )}
      {isVisible('notes') && <NotesSlide />}
      {canSeeTextMessages && isVisible('textMessages') && <TextMessagesSlide />}
      {canSeeIneligiblePartytab && <IneligibleParties />}
      <LoanEstimateDueDateFloater loanNumber={loanNo} />
      <FloaterTabsMinimizeHandles />
      {historyDocument && <DocumentHistoryModal document={historyDocument} onClose={() => setHistoryDocument(null)} />}
      {showAllDocumentHistory && (
        <DocumentHistoryModal document={null} onClose={() => setShowAllDocumentHistory(false)} />
      )}
    </>
  )
}
